<template>
  <div class="mega-menu-content">
    <div class="mega-menu-inner">
      <div class="mega-menu-left">
        <div
          class="mega-menu-columns"
          :style="{
            '--mega-cols': Math.max(textColumns.length, 1),
          }"
        >
          <div
            v-for="(column, index) in textColumns"
            :key="`text-${column.id ?? index}`"
            class="mega-menu-column"
            :class="{
              'mega-menu-column--border':
                index < textColumns.length - 1,
            }"
          >
            <h4 class="mega-menu-title">{{ column.linkLabel }}</h4>

            <ul
              v-if="column.children?.length"
              class="mega-menu-list"
            >
              <li
                v-for="(child, cIndex) in column.children"
                :key="child.id ?? cIndex"
                :class="{
                  'is-emphasis': isEmphasisItem(child),
                }"
              >
                <router-link
                  :to="child.linkTo || '/'"
                  class="mega-menu-link"
                >
                  {{ child.linkLabel }}
                </router-link>
              </li>
            </ul>
          </div>
        </div>

        <!-- Outside UL — centered under text columns -->
        <div class="mega-menu-view-all">
          <router-link
            :to="viewAllLink"
            class="mega-menu-view-all__link"
          >
            {{ $t("text.viewAll") }}
          </router-link>
        </div>
      </div>

      <div class="mega-menu-right">
        <router-link
          v-for="(card, index) in featuredColumns"
          :key="`feat-${card.id ?? index}`"
          :to="card.linkTo || '/'"
          class="mega-image-card"
        >
          <div class="mega-image-card__media">
            <img
              :src="cardImage(card)"
              :alt="card.linkLabel"
              loading="lazy"
            />
          </div>
          <div class="mega-image-card__label">
            {{ card.linkLabel }}
          </div>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

interface MenuNode {
  id?: number | string;
  linkLabel?: string;
  linkTo?: string;
  viewAllTo?: string;
  menuClass?: string;
  icon?: string;
  image?: string;
  urlImage?: string;
  imageBanner?: string;
  children?: MenuNode[];
  [key: string]: any;
}

interface Props {
  megaMenu: MenuNode[];
}

const props = withDefaults(defineProps<Props>(), {
  megaMenu: () => [],
});

const { t } = useI18n();

const FEATURED_FALLBACKS = computed(() => [
  {
    id: "fallback-1",
    linkLabel: t("text.livingRoom"),
    linkTo: "/",
    image:
      "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=600",
  },
  {
    id: "fallback-2",
    linkLabel: t("text.kitchenIdeas"),
    linkTo: "/",
    image:
      "https://images.unsplash.com/photo-1556911220-e15b29be8c8f?w=600",
  },
]);

const cardImage = (item: MenuNode) =>
  item.urlImage ||
  item.imageBanner ||
  item.image ||
  (item.icon && /^https?:\/\//.test(String(item.icon)) ? item.icon : "") ||
  FEATURED_FALLBACKS.value[0].image;

const isFeaturedColumn = (item: MenuNode) => {
  const cls = String(item.menuClass || "").toLowerCase();
  if (
    cls.includes("image") ||
    cls.includes("featured") ||
    cls === "megaimage"
  ) {
    return true;
  }
  return Boolean(
    item.urlImage ||
      item.imageBanner ||
      item.image ||
      (item.icon && /^https?:\/\//.test(String(item.icon)))
  );
};

const textColumns = computed(() =>
  (props.megaMenu || []).filter((item) => !isFeaturedColumn(item))
);

const featuredColumns = computed(() => {
  const fromApi = (props.megaMenu || []).filter((item) =>
    isFeaturedColumn(item)
  );
  const fallbacks = FEATURED_FALLBACKS.value;
  if (fromApi.length >= 2) return fromApi.slice(0, 2);
  if (fromApi.length === 1) return [...fromApi, fallbacks[1]];
  return fallbacks;
});

const viewAllLink = computed(() => {
  const cols = textColumns.value;
  const last = cols[cols.length - 1];
  return (
    last?.viewAllTo ||
    last?.linkTo ||
    cols.find((c) => c.linkTo)?.linkTo ||
    "/products"
  );
});

const isEmphasisItem = (child: MenuNode) => {
  const label = String(child.linkLabel || "").toLowerCase();
  return (
    label.includes("mới") ||
    label.includes("new") ||
    child.menuClass === "emphasis" ||
    child.menuClass === "bold"
  );
};
</script>

<style scoped>
.mega-menu-content {
  position: relative;
  width: 100%;
  padding: 44px 56px 40px;
  overflow: hidden;
}

/* Semi-transparent overlay — page behind shows through */
.mega-menu-content::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 0;
  background-color: var(--primary-dark, #102542);
  opacity: 0.82;
  pointer-events: none;
}

.mega-menu-inner {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: stretch;
  gap: 36px;
  max-width: 1320px;
  margin: 0 auto;
  min-height: 340px;
}

.mega-menu-left {
  flex: 1 1 58%;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.mega-menu-columns {
  display: grid;
  grid-template-columns: repeat(var(--mega-cols, 3), minmax(0, 1fr));
  flex: 1;
  align-items: start;
}

.mega-menu-column {
  padding: 0 32px;
  text-align: left;
}

.mega-menu-column:first-child {
  padding-left: 0;
}

.mega-menu-column:last-child {
  padding-right: 8px;
}

.mega-menu-column--border {
  border-right: 1px solid rgba(255, 255, 255, 0.2);
}

.mega-menu-title {
  margin: 0 0 20px;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #fff;
  line-height: 1.35;
}

.mega-menu-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.mega-menu-list li {
  margin: 0 0 10px;
  line-height: 1.5;
}

.mega-menu-link {
  display: inline-block;
  color: var(--default-header-color, #b19663);
  font-size: 15px;
  font-weight: 400;
  text-decoration: none;
  transition: color 0.2s ease;
}

.mega-menu-link:hover {
  color: #fff;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 4px;
}

.mega-menu-list li.is-emphasis .mega-menu-link {
  font-weight: 700;
  color: #fff;
}

.mega-menu-view-all {
  margin-top: auto;
  padding-top: 36px;
  text-align: center;
}

.mega-menu-view-all__link {
  display: inline-block;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #fff;
  text-decoration: underline;
  text-underline-offset: 8px;
  text-decoration-thickness: 1px;
}

.mega-menu-view-all__link:hover {
  color: var(--default-header-color, #b19663);
}

.mega-menu-right {
  flex: 0 0 40%;
  max-width: 460px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  align-self: stretch;
}

.mega-image-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 300px;
  text-decoration: none;
  overflow: hidden;
  background: #0a1829;
}

.mega-image-card__media {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.mega-image-card__media img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}

.mega-image-card:hover .mega-image-card__media img {
  transform: scale(1.04);
}

.mega-image-card__label {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 52px;
  padding: 14px 12px;
  background: var(--primary-dark, #102542);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-align: center;
  line-height: 1.35;
}

@media (max-width: 1199px) {
  .mega-menu-content {
    padding: 32px 28px 28px;
  }

  .mega-menu-column {
    padding: 0 18px;
  }

  .mega-menu-right {
    flex-basis: 38%;
    max-width: 380px;
  }
}

@media (max-width: 991px) {
  .mega-menu-inner {
    flex-direction: column;
    gap: 24px;
  }

  .mega-menu-columns {
    grid-template-columns: 1fr;
  }

  .mega-menu-column,
  .mega-menu-column--border {
    border-right: none;
    padding: 0 0 18px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  }

  .mega-menu-column:last-child {
    border-bottom: none;
    padding-right: 0;
  }

  .mega-menu-right {
    flex-basis: auto;
    max-width: none;
    width: 100%;
  }

  .mega-image-card {
    min-height: 220px;
  }
}
</style>
